/**
 * BE#648 — translate a migrated (DTSTART;TZID) recurrence rule between its own
 * zone-aware frame and a runtime-TZ-INDEPENDENT "floating" frame, so the calendar
 * EDIT write-back (drag / delete / re-anchor) can reason in floating wall-clock and
 * never touch the browser's own `getTimezoneOffset`.
 *
 * The shared expansion engine (bt-common) and the #410 display/write fixes already
 * work this way: a TZID occurrence's WALL-CLOCK is carried in a Date's UTC fields
 * ("floating"), and luxon maps that wall-clock to the true instant in the zone. These
 * helpers let the legacy, browser-offset handler operate unchanged inside a floating
 * frame: normalise the rule + input instants to floating at the boundary, run the
 * handler (its browser-offset math becomes a no-op because displayed == rule ==
 * floating), then denormalise the result back to true instants + a TZID rule string.
 *
 * Pure + runtime-TZ-independent (`moment.tz` + luxon only; never `getTimezoneOffset`).
 */
import moment from 'moment';
import 'moment-timezone';
import { floatingOccurrenceToInstant } from '@bt-common/utils/recurrence-calculations';

/** The IANA zone of a `DTSTART;TZID=<zone>:...` rule string, or undefined for a
 *  legacy (UTC, no-TZID) rule. */
export const tzidOfRuleString = (ruleStr: string | undefined): string | undefined => {
    const m = /DTSTART;TZID=([^:;\n\r]+):/i.exec(ruleStr ?? '');
    return m ? m[1] : undefined;
};

/** A true instant -> the FLOATING wall-clock Date (wall-clock carried in UTC fields)
 *  for `tzid`. Process-TZ-independent. */
export const instantToFloating = (instant: Date, tzid: string): Date => {
    const z = moment.tz(instant, tzid);
    return new Date(Date.UTC(z.year(), z.month(), z.date(), z.hour(), z.minute(), z.second(), z.millisecond()));
};

/** A FLOATING wall-clock Date -> the true instant it represents in `tzid`. */
export const floatingToInstant = (floating: Date, tzid: string): Date => floatingOccurrenceToInstant(floating, tzid);

/** An ISO string holding a floating wall-clock -> the true instant's ISO, in `tzid`. */
export const floatingIsoToTrueIso = (iso: string, tzid: string): string =>
    floatingToInstant(new Date(iso), tzid).toISOString();

/**
 * Strip the TZID from a rule string, turning its DTSTART/EXDATE wall-clock values
 * into floating UTC (`...Z`) form, AND floatify a stored RRULE-line UNTIL (a true UTC
 * instant) into the floating frame. The resulting rule expands deterministically
 * (rrule floating uses UTC methods), independent of the process clock.
 *
 * The UNTIL step is the inverse of {@link untilFloatingToTrue} (the denormalize step),
 * so an UNTIL the edit does NOT touch round-trips to EXACTLY its original value; without
 * it, denormalize would convert an already-true UNTIL a SECOND time and shift it by the
 * zone offset on every edit, eventually dropping the last send.
 */
export const toFloatingRuleString = (ruleStr: string): string => {
    const tzid = tzidOfRuleString(ruleStr);
    const stripped = ruleStr
        .replace(/DTSTART;TZID=[^:;\n\r]+:(\d{8}T\d{6})/gi, 'DTSTART:$1Z')
        .replace(/EXDATE;TZID=[^:;\n\r]+:([^\n\r]+)/gi, (_m, vals: string) =>
            'EXDATE:' + vals.split(',').map(v => v.trim().replace(/Z$/i, '') + 'Z').join(','));
    return tzid ? (untilTrueToFloating(stripped, tzid) ?? stripped) : stripped;
};

// Parse a compact UTC timestamp (YYYYMMDDTHHMMSS, optional trailing Z) as a FLOATING
// Date (its value carried in the Date's UTC fields).
const parseCompactUtc = (s: string): Date => {
    const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/.exec(s);
    return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6])) : new Date(NaN);
};

const formatCompactUtc = (d: Date): string =>
    d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');

/**
 * Convert a FLOATING UNTIL (the wall-clock value an rrule written in the floating
 * frame carries in its RRULE line) to the TRUE UTC instant the engine reads.
 *
 * The edit handler truncates/splits in the floating frame, so a HEAD rule's UNTIL comes
 * out as wall-clock. Per RFC 5545 (and the shared engine's `getUntilInstant`) UNTIL is a
 * TRUE UTC instant — left unconverted it DUPLICATES the boundary send (east of UTC) or
 * drops the last-day send (west). Only the RRULE line's UNTIL token is rewritten;
 * DTSTART/EXDATE are handled by {@link addTzidToRuleString}.
 */
export const untilFloatingToTrue = (ruleStr: string | undefined, tzid: string): string | undefined => {
    if (!ruleStr) return ruleStr;
    return ruleStr.replace(/UNTIL=(\d{8}T\d{6})Z?/gi, (_m, v: string) =>
        'UNTIL=' + formatCompactUtc(floatingToInstant(parseCompactUtc(v), tzid)));
};

/**
 * Inverse of {@link untilFloatingToTrue}: convert a STORED true-UTC RRULE-line UNTIL
 * into the floating wall-clock frame, so the edit handler reasons about it in the same
 * frame as the (floating) occurrences and an untouched UNTIL round-trips exactly.
 */
export const untilTrueToFloating = (ruleStr: string | undefined, tzid: string): string | undefined => {
    if (!ruleStr) return ruleStr;
    return ruleStr.replace(/UNTIL=(\d{8}T\d{6})Z?/gi, (_m, v: string) =>
        'UNTIL=' + formatCompactUtc(instantToFloating(parseCompactUtc(v), tzid)));
};

/**
 * Re-attach a TZID to a floating (UTC `...Z`) rule string: turn DTSTART/EXDATE back
 * into `;TZID=<zone>:<wall-clock>` form (the RRULE line, incl. any UNTIL, is left
 * untouched — UNTIL is converted separately by {@link untilFloatingToTrue}). Inverse
 * of {@link toFloatingRuleString}.
 */
export const addTzidToRuleString = (ruleStr: string | undefined, tzid: string): string | undefined => {
    if (!ruleStr) return ruleStr;
    return ruleStr
        .replace(/DTSTART:(\d{8}T\d{6})Z/gi, `DTSTART;TZID=${tzid}:$1`)
        .replace(/(^|\n)EXDATE:([^\n\r]+)/gi, (_m, lead: string, vals: string) =>
            `${lead}EXDATE;TZID=${tzid}:` + vals.split(',').map(v => v.trim().replace(/Z$/i, '')).join(','));
};
